import React from 'react'
import { Card, Badge, Modal, Descriptions, Flex, Button, Tabs } from 'antd'
import { useMeasure } from 'react-use'
import ReactPlayer from 'react-player'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import { ToolOutlined } from '@ant-design/icons'

const TOOL_ORDER = [
  'Cover removal',
  'Carrier removal',
  'Secondary lock release',
  'Terminal extraction',
  'Terminal extraction small',
  'Terminal extraction medium',
  'Terminal extraction large',
]

const getToolOrder = (usedFor) => {
  const index = TOOL_ORDER.indexOf(usedFor)
  return index === -1 ? TOOL_ORDER.length : index
}

const sortTools = (tools) =>
  [...tools].sort(
    (a, b) => getToolOrder(a.tool.used_for) - getToolOrder(b.tool.used_for),
  )

// A connector may use the same tool for several steps, so the part number is
// not unique across a pin's tools. Each step carries the id of its used_with
// edge (unique per step); fall back to a composite key for older payloads.
const toolKey = (tool, index) =>
  tool.id != null
    ? String(tool.id)
    : `${tool.tool.part_number}::${tool.tool.used_for || ''}::${index}`

const ToolsButton = ({ tools, pin, isLoading }) => {
  const sortedTools = sortTools(tools)
  const [open, setOpen] = React.useState(false)
  const gridStyle = {
    width: '50%',
    height: '300px',
    boxSizing: 'border-box',
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    //boxShadow: 'none',
  }
  const items = sortedTools.map((tool, index) => ({
    key: toolKey(tool, index),
    label: (
      <>
        <p>
          {tool.tool.part_number}
          {tool.tool.cavities_display && ` (${tool.tool.cavities_display})`}
        </p>
        {tool.tool.used_for}
      </>
    ),
    children: (
      <Flex gap="middle" style={{ height: '300px' }}>
        <Card.Grid style={gridStyle} hoverable={false}>
          <div>
            <p>{tool.tool.part_number}</p>
          </div>
          <ToolImage tool={tool} key={tool.tool.part_number} />
        </Card.Grid>
        <Card.Grid style={gridStyle} hoverable={false}>
          <div>
            <p>{tool.tool.used_for}</p>
          </div>
          {tool.instruction ? (
            <ReactPlayer
              key={tool.instruction.url_high_quality}
              src={tool.instruction.url_high_quality}
              controls={true}
              height="100%"
            />
          ) : (
            <div>No video available</div>
          )}
        </Card.Grid>
      </Flex>
    ),
  }))

  const pinDescription = [
    {
      label: 'Pin',
      children: pin.pin,
    },
  ]

  if (pin.description) {
    pinDescription.push({
      label: 'Description',
      children: pin.description,
    })
  }

  return (
    <>
      <Badge count={tools.length} size="small" color="green">
        <Button
          disabled={tools.length === 0}
          type="default"
          icon={<ToolOutlined />}
          onClick={() => setOpen(true)}
          loading={isLoading}
        />
      </Badge>
      <Modal
        title="Tools"
        open={open}
        onOk={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        width="800px"
        style={{ height: '300px' }}
        footer={null}
      >
        <Descriptions items={pinDescription} />
        <br />
        <Card
          //title="Tool Details"
          styles={{
            body: {
              padding: 0,
              overflow: 'hidden',
            },
          }}
        >
          <Tabs
            items={items}
            size="large"
            tabBarStyle={{ padding: '0 24px', marginBottom: 12 }}
          />
        </Card>
      </Modal>
    </>
  )
}

const ToolImage = ({ tool, scaleUp = true }) => {
  const [containerRef, { width: containerWidth, height: containerHeight }] =
    useMeasure()
  const [imageNaturalWidth, setImageNaturalWidth] = React.useState(0)
  const [imageNaturalHeight, setImageNaturalHeight] = React.useState(0)
  const transformRef = React.useRef(null)
  const imageScale = React.useMemo(() => {
    if (
      containerWidth === 0 ||
      containerHeight === 0 ||
      imageNaturalWidth === 0 ||
      imageNaturalHeight === 0
    )
      return 0
    const scale = Math.min(
      containerWidth / imageNaturalWidth,
      containerHeight / imageNaturalHeight,
    )
    return scaleUp ? scale : Math.max(scale, 1)
  }, [
    scaleUp,
    containerWidth,
    containerHeight,
    imageNaturalWidth,
    imageNaturalHeight,
  ])

  React.useEffect(() => {
    const image = new Image()
    image.onload = () => handleImageOnLoad(image)
    image.src = tool.tool.thumbnail
  }, [tool.tool.thumbnail])

  const handleImageOnLoad = (image) => {
    setImageNaturalWidth(image.naturalWidth)
    setImageNaturalHeight(image.naturalHeight)
  }

  // Center and scale image when dimensions change
  React.useEffect(() => {
    if (transformRef.current && imageScale > 0) {
      transformRef.current.centerView(imageScale, 0)
    }
  }, [imageScale])

  const zoomFactor = 5

  return (
    <div
      ref={containerRef}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {imageScale > 0 && (
        <TransformWrapper
          ref={transformRef}
          limitToBounds={true}
          initialScale={imageScale}
          minScale={imageScale}
          maxScale={imageScale * zoomFactor}
          centerOnInit
        >
          <TransformComponent
            wrapperStyle={{
              height: '100%',
              width: '100%',
            }}
          >
            <img
              src={tool.tool.thumbnail}
              alt={tool.tool.part_number}
              data-testid="tool-thumbnail"
            />
          </TransformComponent>
        </TransformWrapper>
      )}
    </div>
  )
}

export { ToolsButton }
